'use client';

import type { FragmentType } from '@packages/gql/generated/shopping';
import type { ProductPriceFragmentFragmentDoc } from '@packages/gql/generated/shopping/ProductPriceFragmentFragmentDoc';
import { useSearchParams } from 'next/navigation';
import { useIntl } from 'react-intl';
import { unmask } from '@packages/gql/src/betterMasking';
import { usePriceFormatter } from '@packages/shared/src/hooks/usePriceFormatter/usePriceFormatter';
import type { MessageValue } from 'react-intl';
import { getVoucherPriceDiscountPercent } from './getVoucherPriceDiscountPercent/getVoucherPriceDiscountPercent';

export type VoucherPriceProps = {
  data: FragmentType<typeof ProductPriceFragmentFragmentDoc>;
};

/**
 * <p>This component renders a voucher price, also called "Geizhals Einsprung".</p>
 * */
export const VoucherPrice = ({ data }: VoucherPriceProps) => {
  const { value, currency } = unmask<typeof ProductPriceFragmentFragmentDoc>(data);
  const searchParams = useSearchParams();
  const { formatMessage } = useIntl();
  const formatPrice = usePriceFormatter('PRODUCT');

  const voucherPriceDiscount = searchParams ? getVoucherPriceDiscountPercent(searchParams) : null;

  // explicit null is needed here because voucherPriceDiscount can be "0"
  if (voucherPriceDiscount === null) {
    return null;
  }

  return (
    <div>
      <div className="text-size-headline-xl text-color-sale font-bold">
        {formatMessage<{ readonly price: MessageValue }>(
          {
            id: 'voucherprice.discount.pricetext',
            defaultMessage: '{price} mit Ihrem Rabatt',
          },
          {
            price: formatPrice(Math.round(value * ((100 - voucherPriceDiscount) / 100)), currency),
          },
        )}
      </div>
      <div className="text-size-body-xs text-color-text-darktransparent">
        {formatMessage({
          id: 'voucherprice.discount.subtext',
          defaultMessage: 'Ihr Rabatt wird im letzten Bestellschritt automatisch abgezogen.',
        })}
      </div>
    </div>
  );
};
